import _ from "lodash";
import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import "./styles.css";

interface ChangesSavedProps {
  className?: string;
  onSave?: (isManualSave?: boolean) => Promise<void> | void;
  tlStore: any;
  enabled?: boolean;
}

const SavedCheckIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
    <path
      d="M18.3346 9.2355V10.0022C18.3336 11.7992 17.7517 13.5477 16.6757 14.987C15.5998 16.4263 14.0874 17.4792 12.3641 17.9887C10.6408 18.4983 8.79902 18.4371 7.11336 17.8143C5.4277 17.1916 3.98851 16.0406 3.01044 14.5331C2.03236 13.0255 1.56779 11.2422 1.68603 9.44909C1.80427 7.65597 2.49897 5.9491 3.66654 4.58306C4.8341 3.21701 6.41196 2.26498 8.16479 1.86895C9.91763 1.47292 11.7515 1.65411 13.393 2.3855"
      stroke="#107C10"
      strokeWidth="1.25"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M18.3333 3.33203L10 11.6737L7.5 9.1737"
      stroke="#107C10"
      strokeWidth="1.25"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ChangesSaved: React.FC<ChangesSavedProps> = ({ className, onSave, tlStore, enabled = true }) => {
  // Combined state management from both useAutoSaveDesign and ChangesSaved
  const [saveStatus, setSaveStatus] = useState("Saved");
  const lastSaveTime = useRef<number>(0);
  const isSavingRef = useRef<boolean>(false);
  const pendingSaveRef = useRef<boolean>(false);

  // Enhanced save function with better status tracking
  // isManualSave: true for click saves, false for auto-saves
  const handleSave = useCallback(
    async (isManualSave = false) => {
      if (!enabled || isSavingRef.current || !onSave) return;

      try {
        isSavingRef.current = true;
        pendingSaveRef.current = false;
        setSaveStatus("Saving...");

        // Pass isManualSave to generateAndSaveDesign
        await onSave(isManualSave);

        lastSaveTime.current = Date.now();
        setSaveStatus("Saved");

        // Notify Variables panel to update its thumbnail preview
        window.dispatchEvent(new CustomEvent("flyer-builder-save"));
      } catch (error) {
        console.error("Save failed:", error);
        setSaveStatus("Save failed");
        // Revert to saved status after a delay
        setTimeout(() => setSaveStatus("Saved"), 3000);
      } finally {
        isSavingRef.current = false;

        // If there's a pending save request, trigger it
        if (pendingSaveRef.current) {
          debouncedSave();
        }
      }
    },
    [enabled, onSave]
  );

  // Keep a ref to the latest handleSave so the debounced function below can be
  // created exactly once yet always call the current implementation.
  const handleSaveRef = useRef(handleSave);
  handleSaveRef.current = handleSave;

  // Debounced save function with 3 second delay.
  // IMPORTANT: created ONCE (empty deps) and reads handleSave via a ref. The
  // previous version recreated the lodash debounce on every render (because
  // `onSave`/`handleSave` change identity each render). Each stale instance
  // kept its own pending trailing timer, so a burst of rapid edits — e.g.
  // dragging the QR color picker, which writes to the store on every onChange —
  // produced a cascade of independent saves instead of a single debounced one.
  // A single stable instance coalesces all drag updates into ONE save 3s after
  // the last change.
  const debouncedSave = useMemo(
    () =>
      _.debounce(() => {
        if (isSavingRef.current) {
          pendingSaveRef.current = true;
          return;
        }

        handleSaveRef.current(false); // false = auto-save
      }, 3000), // Save after 3 seconds of inactivity
    []
  );

  // Flush nothing / cancel the pending timer on unmount to avoid a save firing
  // against a torn-down editor.
  useEffect(() => {
    return () => {
      debouncedSave.cancel();
    };
  }, [debouncedSave]);

  // Trigger debounced save after each change
  const throttledSave = useCallback(() => {
    debouncedSave();
  }, [debouncedSave]);

  // Listen to tlStore changes for auto-save
  useEffect(() => {
    if (!enabled || !tlStore?.store) return;

    const handleStoreChange = () => {
      // Only show "unsaved changes" if we're not currently saving
      if (!isSavingRef.current) {
        setSaveStatus("Unsaved changes");
      }
      throttledSave();
    };

    // Listen to store changes
    const unsubscribe = tlStore.store.listen(
      ({ changes }: any) => {
        // Only trigger save if there are actual changes
        const hasChanges =
          Object.keys(changes.added).length > 0 ||
          Object.keys(changes.updated).length > 0 ||
          Object.keys(changes.removed).length > 0;

        if (hasChanges) {
          handleStoreChange();
        }
      },
      { source: "user", scope: "document" }
    );

    return () => {
      unsubscribe();
    };
  }, [enabled, tlStore, throttledSave]);

  // Format last save time for display
  const getLastSaveDisplay = () => {
    if (lastSaveTime.current === 0) return null;

    const timeSinceLastSave = Date.now() - lastSaveTime.current;

    if (timeSinceLastSave < 60000) {
      // Less than 1 minute
      return "Just now";
    } else if (timeSinceLastSave < 3600000) {
      // Less than 1 hour
      const minutes = Math.floor(timeSinceLastSave / 60000);
      return `${minutes} ${minutes === 1 ? "min" : "mins"} ago`;
    } else {
      const hours = Math.floor(timeSinceLastSave / 3600000);
      return `${hours}h ago`;
    }
  };

  const lastSaveDisplay = getLastSaveDisplay();

  return (
    <div
      className={`changes-saved ${className || ""} ${isSavingRef.current ? "saving" : ""}`}
      onClick={() => handleSave(true)} // true = manual save (updates global state)
      title={lastSaveDisplay ? `Last saved ${lastSaveDisplay}` : "Click to save manually"}
    >
      <SavedCheckIcon />
      <span className="changes-saved-text">
        {saveStatus === "Saved" && lastSaveDisplay ? `Last saved: ${lastSaveDisplay}` : saveStatus}
        {saveStatus === "Unsaved changes" && <small className="click-to-save-hint"> • click to save</small>}
      </span>
    </div>
  );
};

export default ChangesSaved;
